import {gql} from '@apollo/client';

import {ProteinCreationTableRequest} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {
  AntibodyComponentAndRelatedSchemasQuery,
  AntibodyComponentAndRelatedSchemasQueryVariables,
} from 'antibodies/nested-data-creation/spreadsheet-import/__generated/AntibodyComponentAndRelatedSchemasQuery';
import {
  ProteinSchemaLinkedSchemasQuery,
  ProteinSchemaLinkedSchemasQueryVariables,
} from 'antibodies/nested-data-creation/spreadsheet-import/__generated/ProteinSchemaLinkedSchemasQuery';
import {
  ProteinSchemaValidityQuery,
  ProteinSchemaValidityQueryVariables,
} from 'antibodies/nested-data-creation/spreadsheet-import/__generated/ProteinSchemaValidityQuery';
import {
  AntibodyComponentAndRelatedSchemas,
  NdcColumnInferenceRequest,
  NdcColumnInferenceResponse,
  NdcColumnInferenceResult,
  NdcSpreadsheetImportRequest,
  NdcSpreadsheetImportResponse,
  AntibodyComponentSchemaInfo,
  ProteinCreationSpreadsheetParseRequest,
  ProteinCreationSpreadsheetParseResponse,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {
  AntibodyComponentSchemaRole,
  AntibodyDomainType,
  AntibodyRelatedSchemaRole,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ErrorSubclass from 'util/ErrorSubclass';
import * as TaskRunner from 'util/TaskRunner';

/**
 * Thrown when a protein schema cannot be resolved as a valid antibody schema in its registry.
 */
export class InvalidProteinSchemaError extends ErrorSubclass {
  readonly schemaId: string;
  readonly registryId: string;

  constructor(schemaId: string, registryId: string) {
    super(`Protein schema ${schemaId} is not a valid antibody schema in registry ${registryId}`);
    this.schemaId = schemaId;
    this.registryId = registryId;
  }
}

export function parseProteinCreationSpreadsheet(
  input: ProteinCreationSpreadsheetParseRequest
): Promise<ProteinCreationSpreadsheetParseResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:parse-protein-creation-spreadsheet',
    data: input,
  });
}

export async function inferColumnsForNestedCreateAndRegisterSpreadsheet(
  input: NdcColumnInferenceRequest
): Promise<NdcColumnInferenceResponse> {
  const {
    inferredColumns,
    rowCount,
  }: {
    inferredColumns: Array<NdcColumnInferenceResult>;
    rowCount: number;
  } = await TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:infer-columns-from-nested-create-and-register-spreadsheet',
    data: input,
  });

  return {
    columnTypes: inferredColumns,
    // Backend returns data rows only (header row excluded).
    rowCount,
  };
}

export function nestedCreateAndRegisterFromSpreadsheetImport(
  input: NdcSpreadsheetImportRequest
): Promise<NdcSpreadsheetImportResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:nested-create-and-register-from-spreadsheet',
    data: input,
  });
}

export function createProteinsFromTable(
  request: ProteinCreationTableRequest
): Promise<NdcSpreadsheetImportResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:create-proteins-from-table',
    data: request,
  });
}

const ANTIBODY_COMPONENT_AND_RELATED_SCHEMAS_QUERY = gql`
  query AntibodyComponentAndRelatedSchemasQuery($registryId: StrictString!, $proteinSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        validAntibodySchema(id: $proteinSchemaId) {
          id
          componentSchemas {
            role
            id
            name
            distinctComponentKey {
              ... on DistinctDomainKeyNode {
                label
              }
              ... on DistinctChainKeyNode {
                label
              }
            }
          }
        }
        antibodyRelatedSchemas {
          id
          name
          role
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AntibodyComponentSchema = NonNullable<
  ExcludeInaccessible<AntibodyComponentAndRelatedSchemasQuery['registry']>['validAntibodySchema']
>['componentSchemas'][number];

/** Extracts component schemas by role and deduplicates by the schema ID. Component schemas can contain
 * duplicates because the backend returns one entry per slot in the antibody (e.g., both VH and VL domains may
 * reference the same schema). Exported only for testing. */
export function _getUniqueComponentSchemasWithRole(
  schemas: Array<AntibodyComponentSchema>,
  role: AntibodyComponentSchemaRole
): Array<AntibodyComponentSchemaInfo> {
  const schemasWithRole = schemas.filter((schema) => schema.role === role);
  return [
    ...new Map(schemasWithRole.map((schema) => [schema.id, {id: schema.id, name: schema.name}])).values(),
  ];
}

const PROTEIN_SCHEMA_VALIDITY_QUERY = gql`
  query ProteinSchemaValidityQuery($registryId: StrictString!, $proteinSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        validAntibodySchema(id: $proteinSchemaId) {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface QueryProteinSchemaValidityInput {
  registryId: string;
  proteinSchemaId: string;
}

/**
 * Validates that a protein schema resolves as a valid antibody schema, throwing InvalidProteinSchemaError
 * if not. A schema can be selectable (unarchived) yet fail antibody validation, so we check on selection.
 */
export async function queryProteinSchemaValidity({
  registryId,
  proteinSchemaId,
}: QueryProteinSchemaValidityInput): Promise<void> {
  const result = await doQuerySafe<ProteinSchemaValidityQuery, ProteinSchemaValidityQueryVariables>(
    {query: PROTEIN_SCHEMA_VALIDITY_QUERY},
    // Force a network read: validity can change between selections, so a cache-first read could decide on
    // stale data.
    {variables: {registryId, proteinSchemaId}, fetchPolicy: 'network-only'}
  );

  const registry = result.registry;
  if (isInaccessible(registry)) {
    throw new Error(`Registry ${registryId} is inaccessible`);
  }
  if (registry.validAntibodySchema == null) {
    throw new InvalidProteinSchemaError(proteinSchemaId, registryId);
  }
}

export interface QueryAntibodyComponentAndRelatedSchemasInput {
  registryId: string;
  proteinSchemaId: string;
}

export async function queryAntibodyComponentAndRelatedSchemas({
  registryId,
  proteinSchemaId,
}: QueryAntibodyComponentAndRelatedSchemasInput): Promise<AntibodyComponentAndRelatedSchemas> {
  const result = await doQuerySafe<
    AntibodyComponentAndRelatedSchemasQuery,
    AntibodyComponentAndRelatedSchemasQueryVariables
  >({query: ANTIBODY_COMPONENT_AND_RELATED_SCHEMAS_QUERY}, {variables: {registryId, proteinSchemaId}});

  const registry = result.registry;
  if (isInaccessible(registry)) {
    throw new Error(`Registry ${registryId} is inaccessible`);
  }

  const antibodySchema = registry.validAntibodySchema;
  if (antibodySchema == null) {
    throw new InvalidProteinSchemaError(proteinSchemaId, registryId);
  }

  const aaDomainSchemas = _getUniqueComponentSchemasWithRole(
    antibodySchema.componentSchemas,
    AntibodyComponentSchemaRole.AA_DOMAIN
  );
  const aaChainSchemas = _getUniqueComponentSchemasWithRole(
    antibodySchema.componentSchemas,
    AntibodyComponentSchemaRole.AA_CHAIN
  );

  const antibodyRelatedSchemas = registry.antibodyRelatedSchemas;
  const dnaDomainSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.DNA_DOMAIN).map(({id}) => id)
  );
  const dnaChainSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.DNA_CHAIN).map(({id}) => id)
  );
  const dnaPairSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.DNA_PAIR).map(({id}) => id)
  );
  const aaPairSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.AA_PAIR).map(({id}) => id)
  );

  return {
    aaDomainSchemas,
    aaChainSchemas,
    dnaDomainSchemaIds,
    dnaChainSchemaIds,
    dnaPairSchemaIds,
    aaPairSchemaIds,
  };
}

// ---------------------------------------------------------------------------
// SSI linked-schema query: maps each domain/chain to its configured AA schema
// ---------------------------------------------------------------------------

interface DomainLinkedSchema {
  domainType: AntibodyDomainType;
  specificity: string | null;
  schemaOrInterfaceId: string | null;
}

interface ChainLinkedSchema {
  label: string;
  schemaOrInterfaceId: string | null;
}

const PROTEIN_SCHEMA_LINKED_SCHEMAS_QUERY = gql`
  query ProteinSchemaLinkedSchemasQuery($schemaId: StrictString!) @engComponent(name: MOLBIO) @skipAudit {
    implementedAntibodySchemaInterface(schemaId: $schemaId) {
      ...ImplementedAntibodySchemaInterfaceGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ImplementedAntibodySchemaInterface').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProteinSchemaLinkedSchemas {
  domainLinkedSchemas: Array<DomainLinkedSchema>;
  chainLinkedSchemas: Array<ChainLinkedSchema>;
}

export async function queryProteinSchemaLinkedSchemas(
  proteinSchemaId: string
): Promise<ProteinSchemaLinkedSchemas | null> {
  const result = await doQuerySafe<ProteinSchemaLinkedSchemasQuery, ProteinSchemaLinkedSchemasQueryVariables>(
    {query: PROTEIN_SCHEMA_LINKED_SCHEMAS_QUERY},
    {variables: {schemaId: proteinSchemaId}}
  );

  const schemaInterface = result.implementedAntibodySchemaInterface;
  if (schemaInterface == null || isInaccessible(schemaInterface)) {
    return null;
  }
  return schemaInterface;
}
